import { format, isValid } from 'date-fns';
import { IONote } from '../io/io-note.class';
import {
  APPOINTMENT_ENTITY_SET,
  CONTACT_ENTITY_SET,
  CURRENCY_ENTITY_SET,
  EXPENSE_DATE_IS_USER_LOCAL,
  EXPENSE_STATE,
  EXPENSE_STATE_FOR_STATUS,
  EXPENSE_STATUS_CODE,
  EXPENSE_SUBTYPE_ENTITY_SET,
  EXPENSE_TYPE_ENTITY_SET,
  MEETING_EXPENSE_CATEGORY,
  MEETING_EXPENSE_FIELDS,
  MEETING_EXPENSE_NAME_MAX_LENGTH,
  MEETING_EXPENSE_NAV,
} from './meeting-expense.constants';

export enum ExpenseStatus {
  Draft = 'Draft',
  Submitted = 'Submitted',
  InReview = 'InReview',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Cancelled = 'Cancelled',
}

export const EXPENSE_STATUS_LABEL_KEYS: Record<ExpenseStatus, string> = {
  [ExpenseStatus.Draft]: 'OM_DRAFT',
  [ExpenseStatus.Submitted]: 'SUBMITTED',
  [ExpenseStatus.InReview]: 'POP_IN_REVIEV',
  [ExpenseStatus.Approved]: 'APPROVED',
  [ExpenseStatus.Rejected]: 'REJECTED',
  [ExpenseStatus.Cancelled]: 'CANCELLED',
};

export interface ExpenseOption {
  id: string;
  name: string;
}

export interface ExpenseTypeSubtypePair {
  typeId: string;
  subtypeId: string;
}

export interface ExpenseMasterData {
  types: ExpenseOption[];
  subtypes: ExpenseOption[];
  pairs: ExpenseTypeSubtypePair[];
}

export interface MeetingExpense {
  id: string;
  meetingId: string;
  ownerName: string;
  /** yyyy-MM-dd (indskr_date is date-only, user-local) */
  expenseDate: string | null;
  contactId: string | null;
  contactName: string | null;
  typeId: string | null;
  typeName: string | null;
  subtypeId: string | null;
  subtypeName: string | null;
  amount: number | null;
  statusCode: number;
  /** Server `statuscode` display label; cleared when the device changes the status, so the code-based fallback applies. */
  statusLabel?: string;
  serverName: string | null;
  notes: IONote[];
  /** True while the device holds changes the server has not seen. */
  pendingSync?: boolean;
}

const FORMATTED = '@OData.Community.Display.V1.FormattedValue';

const lookupId = (raw: Record<string, any>, field: string): string | null => raw[`_${field}_value`] ?? null;
const lookupName = (raw: Record<string, any>, field: string): string | null => raw[`_${field}_value${FORMATTED}`] ?? null;

export function expenseDateFromDynamics(value: unknown): string | null {
  if (!value) return null;
  const text = String(value);
  if (text.includes('T')) {
    const parsed = new Date(text);
    return isValid(parsed) ? format(parsed, 'YYYY-MM-DD') : null;
  }
  return text.slice(0, 10);
}

export function expenseDateToDynamics(date: string | null): string | null {
  if (!date) return null;
  const day = date.slice(0, 10);
  return EXPENSE_DATE_IS_USER_LOCAL ? new Date(`${day}T00:00:00`).toISOString() : day;
}

export function expenseStatus(expense: Pick<MeetingExpense, 'statusCode'>): ExpenseStatus {
  switch (expense.statusCode) {
    case EXPENSE_STATUS_CODE.CANCELLED: return ExpenseStatus.Cancelled;
    case EXPENSE_STATUS_CODE.SUBMITTED: return ExpenseStatus.Submitted;
    case EXPENSE_STATUS_CODE.FOR_REVIEW: return ExpenseStatus.InReview;
    case EXPENSE_STATUS_CODE.APPROVED: return ExpenseStatus.Approved;
    case EXPENSE_STATUS_CODE.REJECTED: return ExpenseStatus.Rejected;
    default: return ExpenseStatus.Draft;
  }
}

export function expenseDisplayName(expense: MeetingExpense, untitledLabel = 'Expense'): string {
  const owner = expense.ownerName?.trim();
  const customer = expense.contactName?.trim();
  if (!expense.typeName?.trim()) {
    const who = customer || owner;
    return who ? `${untitledLabel} - ${who}` : expense.serverName || untitledLabel;
  }
  const parts = customer ? [customer, expense.typeName, expense.subtypeName] : [expense.typeName, expense.subtypeName, owner];
  return parts.filter((part) => !!part?.trim()).join(' - ');
}

export function meetingExpenseFromRaw(raw: Record<string, any>, meetingId: string, notes: IONote[] = []): MeetingExpense {
  const statusCode = Number(raw[MEETING_EXPENSE_FIELDS.STATUS] ?? EXPENSE_STATUS_CODE.DRAFT);
  return {
    id: raw[MEETING_EXPENSE_FIELDS.ID],
    meetingId,
    ownerName: lookupName(raw, MEETING_EXPENSE_FIELDS.OWNER) ?? '',
    expenseDate: expenseDateFromDynamics(raw[MEETING_EXPENSE_FIELDS.DATE]),
    contactId: lookupId(raw, MEETING_EXPENSE_FIELDS.CONTACT),
    contactName: lookupName(raw, MEETING_EXPENSE_FIELDS.CONTACT),
    typeId: lookupId(raw, MEETING_EXPENSE_FIELDS.TYPE),
    typeName: lookupName(raw, MEETING_EXPENSE_FIELDS.TYPE),
    subtypeId: lookupId(raw, MEETING_EXPENSE_FIELDS.SUBTYPE),
    subtypeName: lookupName(raw, MEETING_EXPENSE_FIELDS.SUBTYPE),
    amount: raw[MEETING_EXPENSE_FIELDS.AMOUNT] ?? null,
    statusCode,
    statusLabel: statusCode === EXPENSE_STATUS_CODE.ACTIVE ? undefined : raw[`${MEETING_EXPENSE_FIELDS.STATUS}${FORMATTED}`],
    serverName: raw[MEETING_EXPENSE_FIELDS.NAME] ?? null,
    notes,
  };
}

const bind = (navProperty: string, entitySet: string, id: string | null): Record<string, string | null> => ({
  [`${navProperty}@odata.bind`]: id ? `/${entitySet}(${id})` : null,
});

export function buildExpenseFieldPayload(expense: MeetingExpense): Record<string, any> {
  return {
    [MEETING_EXPENSE_FIELDS.NAME]: expenseDisplayName(expense).slice(0, MEETING_EXPENSE_NAME_MAX_LENGTH),
    [MEETING_EXPENSE_FIELDS.DATE]: expenseDateToDynamics(expense.expenseDate),
    [MEETING_EXPENSE_FIELDS.AMOUNT]: expense.amount,
    ...bind(MEETING_EXPENSE_NAV.TYPE, EXPENSE_TYPE_ENTITY_SET, expense.typeId),
    ...bind(MEETING_EXPENSE_NAV.SUBTYPE, EXPENSE_SUBTYPE_ENTITY_SET, expense.subtypeId),
    ...bind(MEETING_EXPENSE_NAV.CONTACT, CONTACT_ENTITY_SET, expense.contactId),
  };
}

export function buildExpenseCreatePayload(expense: MeetingExpense, currencyId: string | null): Record<string, any> {
  const fields = Object.fromEntries(
    Object.entries(buildExpenseFieldPayload(expense)).filter(([key, value]) => !(key.endsWith('@odata.bind') && value === null)),
  );
  const payload: Record<string, any> = {
    [MEETING_EXPENSE_FIELDS.ID]: expense.id,
    [MEETING_EXPENSE_FIELDS.STATE]: EXPENSE_STATE.ACTIVE,
    [MEETING_EXPENSE_FIELDS.STATUS]: EXPENSE_STATUS_CODE.DRAFT,
    [MEETING_EXPENSE_FIELDS.CATEGORY]: MEETING_EXPENSE_CATEGORY,
    ...bind(MEETING_EXPENSE_NAV.REGARDING, APPOINTMENT_ENTITY_SET, expense.meetingId),
    ...fields,
  };
  if (currencyId) payload[`${MEETING_EXPENSE_NAV.CURRENCY}@odata.bind`] = `/${CURRENCY_ENTITY_SET}(${currencyId})`;
  return payload;
}

const buildStatusPayload = (statusCode: number): Record<string, number> => ({
  [MEETING_EXPENSE_FIELDS.STATE]: EXPENSE_STATE_FOR_STATUS[statusCode],
  [MEETING_EXPENSE_FIELDS.STATUS]: statusCode,
});

export const buildExpenseSubmitPayload = (): Record<string, number> => buildStatusPayload(EXPENSE_STATUS_CODE.SUBMITTED);

export const buildExpenseScrapPayload = (): Record<string, number> => buildStatusPayload(EXPENSE_STATUS_CODE.CANCELLED);

export function applySubmit(expense: MeetingExpense): MeetingExpense {
  return { ...expense, statusCode: EXPENSE_STATUS_CODE.SUBMITTED, statusLabel: undefined };
}

export function applyScrap(expense: MeetingExpense): MeetingExpense {
  return { ...expense, statusCode: EXPENSE_STATUS_CODE.CANCELLED, statusLabel: undefined };
}

export const isExpenseCancelled = (expense: Pick<MeetingExpense, 'statusCode'>): boolean =>
  expense.statusCode === EXPENSE_STATUS_CODE.CANCELLED;
